import { useState } from "react";
import { Box, Typography, Button, Divider } from "@mui/material";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import { UI_RULES } from "../../config/uiVisibilityRules";
import AnnotationTest from "./AnnotationTest";

const MODES = ["default", ...Object.keys(UI_RULES)];
interface DevModeOptionsProps {
  modes?: string[];
  onExit?: () => void;
}

export default function DevModeOptions({ modes = MODES, onExit }: DevModeOptionsProps) {
  const [mode, setMode] = useState(() => localStorage.getItem("appModeOverride") ?? "default");

  const applyMode = (next: string) => {
    setMode(next);
    localStorage.setItem("appModeOverride", next);
    document.querySelector("[data-app-mode]")?.setAttribute("data-app-mode", next);
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "14px",
        p: 2.5,
        backgroundColor: "rgba(250,250,252,0.9)",
        backdropFilter: "blur(20px)",
        border: "1px solid var(--dk-border, rgba(0,0,0,0.08))",
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <ScienceRoundedIcon sx={{ color: "#007AFF", fontSize: 20 }} />
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--dk-text, #1d1d1f)" }}>개발자 모드</Typography>
        <Box
          sx={{
            ml: 0.5,
            px: 0.9,
            py: 0.2,
            borderRadius: "6px",
            fontSize: 10.5,
            fontWeight: 600,
            color: "#28a745",
            backgroundColor: "rgba(40,200,64,0.14)",
          }}
        >
          ON
        </Box>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "var(--dk-text-2, #86868b)", mb: 2 }}>
        APP_MODE 를 전환해 UI 규칙 적용을 테스트합니다.
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color: "var(--dk-text-2, #86868b)",
          mb: 0.75,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        APP_MODE
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          p: 0.5,
          borderRadius: "10px",
          backgroundColor: "rgba(0,0,0,0.05)",
        }}
      >
        {modes.map((m) => {
          const selected = mode === m;
          return (
            <Box
              key={m}
              onClick={() => applyMode(m)}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 0.85,
                borderRadius: "7px",
                fontSize: 13,
                fontWeight: selected ? 600 : 500,
                cursor: "pointer",
                userSelect: "none",
                color: selected ? "#1d1d1f" : "#6e6e73",
                backgroundColor: selected ? "#fff" : "transparent",
                boxShadow: selected ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                transition: "all 0.15s ease",
              }}
            >
              {m}
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: 11.5, color: "var(--dk-text-2, #86868b)", mt: 1.25 }}>
        현재 모드: <b style={{ color: "var(--dk-text, #1d1d1f)" }}>{mode}</b>
        <br />
        CSS 기반 규칙은 즉시 반영됩니다. 훅(실제 disabled/언마운트) 기반 컴포넌트는 새로고침 후 반영됩니다.
      </Typography>

      <Divider />
      <AnnotationTest />
      {/* 종료 버튼 */}
      {onExit && (
        <Button
          fullWidth
          onClick={onExit}
          disableElevation
          sx={{
            mt: 2,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            py: 0.9,
            borderRadius: "9px",
            color: "#ff3b30",
            backgroundColor: "rgba(255,59,48,0.08)",
            "&:hover": { backgroundColor: "rgba(255,59,48,0.14)" },
          }}
        >
          개발자 모드 종료
        </Button>
      )}
    </Box>
  );
}
